import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { AccordionComponent, ButtonComponent, ListTableComponent, ColumnType } from '@ppl/ui';
import { UserService } from 'src/app/services/user.service';
import { SicktimeService } from 'src/app/services/sicktime.service';
import { Provider, SickTimeBalance, BalancesRequest } from 'src/app/interfaces/sicktime.interface';
import { AuthorizeDirective } from 'src/app/shared/authorization/authorize.directive.';
import { MEMBER_VIEW_PAGE_IDS, TABS } from 'src/app/shared/constants/page.constants';
import { NgZorroModule } from 'src/app/shared/modules/ng-zorro/ng-zorro.module';

@Component({
  selector: 'app-participant-sick-time',
  standalone: true,
  templateUrl: './participant-sick-time.component.html',
  styleUrls: ['./participant-sick-time.component.css'],
  imports: [
    CommonModule,
    AccordionComponent,
    ButtonComponent,
    ListTableComponent,
    AuthorizeDirective,
    NgZorroModule,
    ReactiveFormsModule
  ]
})
export class ParticipantSickTimeComponent implements OnInit {
  searchForm!: FormGroup;
  participantId: string = '';
  employers: Provider[] = [];
  balances: SickTimeBalance[] = [];
  formRows: FormArray = this.fb.array([]);
  loading: boolean = false;

    tableColumns: ColumnType[] = [
        { field: 'year', header: 'Year', width: '15%' },
        { field: 'providerPplId', header: 'Provider PPL ID', width: '15%' },
        { field: 'providerName', header: 'Provider', width: '20%' },
        { field: 'employerPplId', header: 'Employer PPL ID', width: '15%' },
        { field: 'employerName', header: 'Employer', width: '20%' },
        { field: 'code', header: 'Type', width: '10%' },
        { field: 'annualCap', header: 'Cap', width: '10%' },
        { field: 'accrual', header: 'Accrued (Hrs)', width: '15%' },
        { field: 'used', header: 'Used (Hrs)', width: '15%' },
        { field: 'balance', header: 'Balance (Hrs)', width: '15%' },
        { field: 'balanceToUse', header: 'Available to Use (Hrs)', width: '10%' },
    ];

  // Properties used by template
  pageId: string = '';
  tabId: string = TABS.SickTime;
  programName: string = '';

  constructor(private fb: FormBuilder,
              private userService: UserService,
              private sicktimeService: SicktimeService
  ) {}

  ngOnInit(): void {
    this.searchForm = this.fb.group({
      dateRange: [null],
      employer: [null]
    });
    const selectedProgram = this.userService.getSelectedProgram();
    this.participantId = JSON.parse(this.userService.getSelectedLead()!);
    this.pageId = MEMBER_VIEW_PAGE_IDS[this.userService.getFirstSegment()!];
    this.programName = selectedProgram?.program_code ?? '';
    
    // Load employers and initial data for participant
    if (this.participantId) {
      this.loadEmployers();
      this.loadBalancesData();
    }
  }

  loadEmployers(): void {
    this.sicktimeService.getProviders(this.programName, this.participantId).subscribe({
      next: (response) => {
        this.employers = response.responsedata || [];
        console.log('Employers loaded', this.employers);
      },
      error: (error) => {
        console.error('Error loading employers', error);
        this.employers = [];
      }
    });
  }

  filterBalanceSearch(): void {
    const formValue = this.searchForm.value;
    let fromDate: string | null = null;
    let toDate: string | null = null;
    const providerId = formValue.employer || null;
    
    if (formValue.dateRange && formValue.dateRange[0] && formValue.dateRange[1]) {
      fromDate = this.formatDate(formValue.dateRange[0]);
      toDate = this.formatDate(formValue.dateRange[1]);
    }
    
    this.loadBalancesWithFilters(fromDate, toDate, providerId);
  }

  loadBalancesData(): void {
    if (!this.participantId) {
      console.error('Participant ID not available');
      return;
    }

    // Reset the search form
    this.searchForm.reset();

    // Load data using unfiltered endpoint
    this.loadBalances(null, null);
  }

  loadBalancesWithFilters(fromDate: string | null, toDate: string | null, providerId: number | null = null): void {
    if (!this.participantId) {
      console.error('Participant ID not available');
      return;
    }

    this.loading = true;
    const request: BalancesRequest = {
      participantId: this.participantId,
      fromDate,
      toDate
    };

    // Add providerId if provided
    if (providerId) {
      request.providerId = providerId;
    }

    // Always use the filtered endpoint for search functionality
    this.sicktimeService.getBalancesByParticipantAndProvider(this.programName, request).subscribe({
      next: (response) => {
        this.balances = response.responsedata || [];
        this.populateFormRows();
        this.loading = false;
        console.log('Filtered sick time balances loaded', this.balances);
      },
      error: (error) => {
        console.error('Error loading filtered sick time balances', error);
        this.balances = [];
        this.formRows = this.fb.array([]);
        this.loading = false;
      }
    });
  }

  loadBalances(fromDate: string | null, toDate: string | null): void {
    if (!this.participantId) {
      console.error('Participant ID not available');
      return;
    }

    this.loading = true;
    // Use unfiltered endpoint without providerId
    this.sicktimeService.getBalances(this.programName, {
      participantId: this.participantId,
      fromDate,
      toDate
    }).subscribe({
      next: (response) => {
        this.balances = response.responsedata || [];
        this.populateFormRows();
        this.loading = false;
        console.log('Sick time balances loaded (unfiltered)', this.balances);
      },
      error: (error) => {
        console.error('Error loading sick time balances', error);
        this.balances = [];
        this.formRows = this.fb.array([]);
        this.loading = false;
      }
    });
  }

  populateFormRows(): void {
    const formatCurrency = (val: any) => {
      if (val === null || val === undefined) return null;
      const num = Number(val);
      if (isNaN(num)) return val;
      return `$${num.toFixed(2)}`;
    };
    // Build a NEW FormArray and reassign. ppl-list-table's ngOnChanges only fires when the
    // @Input reference changes; mutating the existing array with clear()/push() leaves the
    // reference identical, so the grid never re-renders after the API resolves.
    const rows = this.balances.map(balance => this.fb.group({
      providerPplId: [balance.providerPplId],
      employerPplId: [balance.employerPplId],
      employerName: [balance.employerName],
      providerName: [balance.providerName],
      code: [balance.code],
      accrual: [balance.accrual],
      used: [balance.used],
      balance: [balance.balance],
      year: [balance.year],
      balanceToUse: [balance.balanceToUse],
      annualCap: [balance.annualCap]
    }));
    this.formRows = this.fb.array(rows);
  }

  formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
